import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  detectPlatform,
  isMobile,
  storeHrefForPlatform,
} from '../src/utils/burokLinkDeeplink';

const BUROK_LOGO = '/assets/images/BUROKLOGOTWITTER.png';
const BUROK_BACKGROUND = '/assets/images/Burok gradient.png';

type OpenInAppInterstitialProps = {
  /** Deeplink natif ou URL https à ouvrir. */
  openUrl: string;
  /** Si défini (ex. pay.burok.app), redirige le web vers cette URL. */
  webRedirect?: string;
  /** Titre optionnel. */
  title?: string;
  /** Sous-titre optionnel. */
  subtitle?: string;
  /** Affiche un spinner de chargement (résolution short link). */
  loading?: boolean;
  /** Message d’erreur soft (lien introuvable, etc.). */
  errorMessage?: string | null;
};

/**
 * Interstitiel unique : même design que link.burok.app / BurokLinkShortOpenPage.
 */
export default function OpenInAppInterstitial({
  openUrl,
  webRedirect,
  title = 'Ouverture de Burok…',
  subtitle = 'Si rien ne se passe, l’application n’est peut‑être pas installée.',
  loading = false,
  errorMessage = null,
}: OpenInAppInterstitialProps) {
  const [platform] = useState(() => detectPlatform());
  const storeHref = storeHrefForPlatform(platform);
  const mobile = isMobile();

  const effectiveOpen = useMemo(() => {
    if (webRedirect && !mobile) return webRedirect;
    return openUrl;
  }, [openUrl, webRedirect, mobile]);

  useEffect(() => {
    if (loading || !effectiveOpen) return;

    // Cible paiement / web : redirect navigateur
    if (webRedirect?.startsWith('http')) {
      window.location.href = webRedirect;
      return;
    }

    if (!mobile) return;
    window.location.href = openUrl;
  }, [loading, effectiveOpen, openUrl, webRedirect, mobile]);

  if (loading) {
    return (
      <div
        className="flex flex-col items-center justify-center min-h-screen p-6 text-white bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url("${BUROK_BACKGROUND}")` }}
      >
        <img
          src={BUROK_LOGO}
          alt="Burok"
          className="w-20 h-20 mb-6 rounded-2xl shadow-lg object-contain"
        />
        <Loader2 className="w-10 h-10 text-cyan-400 animate-spin mb-4" />
        <p className="text-center text-white font-medium drop-shadow-sm">Résolution du lien…</p>
      </div>
    );
  }

  if (!mobile) {
    const qrData = openUrl.startsWith('burokapp://') ? openUrl : effectiveOpen;
    const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrData)}`;
    return (
      <div
        className="flex flex-col items-center justify-center min-h-screen p-6 text-white bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url("${BUROK_BACKGROUND}")` }}
      >
        <img
          src={BUROK_LOGO}
          alt="Burok"
          className="w-20 h-20 mb-6 rounded-2xl shadow-lg object-contain"
        />
        <div className="w-full max-w-sm p-6 text-center rounded-2xl border border-white/25 bg-white/95 text-gray-900 shadow-xl mb-8">
          <p className="mb-4 font-semibold text-gray-900">Scannez avec votre téléphone</p>
          <div className="flex justify-center p-4 mb-4 bg-white rounded-xl border border-gray-100">
            <img src={qrSrc} alt="QR Code — ouvrir dans Burok" className="w-48 h-48" />
          </div>
          <p className="text-sm text-gray-600 leading-relaxed">
            Ouvrez l’application Burok sur votre téléphone et scannez ce code pour ouvrir ce lien.
          </p>
          {errorMessage && (
            <p className="mt-3 text-sm text-amber-700">{errorMessage}</p>
          )}
        </div>
        {webRedirect && (
          <a
            href={webRedirect}
            className="w-full max-w-sm mb-3 inline-flex items-center justify-center px-4 py-3 rounded-xl bg-white text-neutral-900 font-medium hover:bg-neutral-100 transition-colors shadow-lg"
          >
            Continuer sur le web
          </a>
        )}
        <a
          href={storeHref}
          className="w-full max-w-sm inline-flex items-center justify-center gap-3 px-4 py-3 rounded-xl bg-black text-white font-medium hover:bg-neutral-900 transition-colors shadow-lg"
        >
          <img
            src={BUROK_LOGO}
            alt=""
            className="w-8 h-8 rounded-lg object-contain shrink-0"
            aria-hidden
          />
          Installer Burok
        </a>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col items-center justify-center min-h-screen p-6 text-white bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url("${BUROK_BACKGROUND}")` }}
    >
      <img
        src={BUROK_LOGO}
        alt="Burok"
        className="w-20 h-20 mb-6 rounded-2xl shadow-lg object-contain"
      />
      <Loader2 className="w-10 h-10 text-cyan-400 animate-spin mb-4" />
      <p className="text-center text-white font-medium mb-2 drop-shadow-sm">{title}</p>
      <p className="text-center text-white text-sm max-w-sm mb-4 leading-relaxed drop-shadow-md">
        {subtitle}
      </p>
      {errorMessage && (
        <p className="text-center text-amber-200 text-sm max-w-sm mb-6">{errorMessage}</p>
      )}
      <div className="flex flex-col sm:flex-row gap-3 w-full max-w-sm">
        <a
          href={openUrl}
          className="flex-1 inline-flex items-center justify-center px-4 py-3 rounded-xl bg-white text-neutral-900 font-medium hover:bg-neutral-100 transition-colors shadow-lg"
        >
          Ouvrir dans l’app
        </a>
        <a
          href={storeHref}
          className="flex-1 inline-flex items-center justify-center gap-3 px-4 py-3 rounded-xl bg-black text-white font-medium hover:bg-neutral-900 transition-colors shadow-lg"
        >
          <img
            src={BUROK_LOGO}
            alt=""
            className="w-8 h-8 rounded-lg object-contain shrink-0"
            aria-hidden
          />
          Installer Burok
        </a>
      </div>
    </div>
  );
}
